import { useEffect, useRef, useState } from "react";
import { MoreHorizontal, Trash2, Pencil, Bookmark, BookmarkCheck, EyeOff, Flag, Link2, X } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";

/**
 * Facebook-style three-dot menu rendered in a post header.
 *
 * Items:
 *   Owner: Edit · Delete · Copy link
 *   Viewer: Save / Unsave · Hide · Report · Copy link
 *
 * The Edit & Report actions open lightweight inline dialogs.
 */
export default function PostMenu({ post, onUpdated, onDeleted, onHidden, onEdit }) {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [reportOpen, setReportOpen] = useState(false);
  const wrapRef = useRef(null);

  const isOwner = user && user.id === post.userId && !post.sponsored;
  const isAdmin = user?.role === "ADMIN";
  const canDelete = isOwner || isAdmin;
  const saved = !!(user && (post.savedBy || []).includes(user.id));

  useEffect(() => {
    const onDoc = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    if (open) document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  const close = () => setOpen(false);

  const handleDelete = async () => {
    if (!window.confirm("Delete this post? This cannot be undone.")) return;
    try {
      await api.delete(`/posts/${post.id}`);
      toast.success("Post deleted");
      onDeleted?.(post.id);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not delete");
    }
    close();
  };

  const handleSave = async () => {
    if (!user) return toast.error("Sign in to save");
    try {
      const { data } = await api.post(`/posts/${post.id}/save`);
      const nextSaved = !!data.saved;
      const savedBy = (post.savedBy || []).filter((id) => id !== user.id);
      if (nextSaved) savedBy.push(user.id);
      onUpdated?.({ ...post, savedBy });
      toast.success(nextSaved ? "Saved" : "Unsaved");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
    close();
  };

  const handleHide = async () => {
    try {
      await api.post(`/posts/${post.id}/hide`);
      toast.success("Hidden from your feed");
      onHidden?.(post.id);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
    close();
  };

  const handleCopyLink = async () => {
    const url = `${window.location.origin}/feed?post=${post.id}`;
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Link copied");
    } catch {
      toast.error("Copy failed");
    }
    close();
  };

  const handleEditClick = () => {
    onEdit?.();
    close();
  };

  const handleReportOpen = () => {
    setReportOpen(true);
    close();
  };

  return (
    <div ref={wrapRef} className="relative" data-testid={`post-menu-${post.id}`}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="p-1.5 rounded-full hover:bg-secondary/10 text-foreground/70 hover:text-foreground transition-colors"
        aria-label="Post menu"
        data-testid={`post-menu-btn-${post.id}`}
      >
        <MoreHorizontal size={18} strokeWidth={1.5} />
      </button>

      {open && (
        <div
          className="absolute right-0 top-full mt-1 w-56 bg-card border border-secondary/25 rounded-sm shadow-xl z-30 overflow-hidden"
          role="menu"
          data-testid={`post-menu-popover-${post.id}`}
        >
          {isOwner && (
            <MenuItem icon={Pencil} label="Edit post" onClick={handleEditClick} testid={`post-menu-edit-${post.id}`} />
          )}
          {!isOwner && user && (
            <MenuItem
              icon={saved ? BookmarkCheck : Bookmark}
              label={saved ? "Unsave post" : "Save post"}
              onClick={handleSave}
              testid={`post-menu-save-${post.id}`}
            />
          )}
          {!isOwner && user && (
            <MenuItem icon={EyeOff} label="Hide this post" onClick={handleHide} testid={`post-menu-hide-${post.id}`} />
          )}
          <MenuItem icon={Link2} label="Copy link" onClick={handleCopyLink} testid={`post-menu-copy-${post.id}`} />
          {!isOwner && user && (
            <MenuItem icon={Flag} label="Report post" onClick={handleReportOpen} testid={`post-menu-report-${post.id}`} danger />
          )}
          {canDelete && (
            <MenuItem icon={Trash2} label="Delete post" onClick={handleDelete} testid={`post-menu-delete-${post.id}`} danger />
          )}
        </div>
      )}

      {reportOpen && (
        <ReportDialog
          postId={post.id}
          onClose={() => setReportOpen(false)}
        />
      )}
    </div>
  );
}

function MenuItem({ icon: Icon, label, onClick, testid, danger }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`w-full flex items-center gap-3 px-4 py-2.5 text-sm text-left transition-colors ${
        danger ? "text-destructive hover:bg-destructive/10" : "text-foreground/90 hover:bg-secondary/10"
      }`}
      data-testid={testid}
    >
      <Icon size={15} strokeWidth={1.5} />
      <span>{label}</span>
    </button>
  );
}

const REPORT_REASONS = [
  "Spam",
  "Harassment or bullying",
  "Hate speech or symbols",
  "Nudity or sexual content",
  "Violence or gore",
  "False information",
  "Scam or fraud",
  "Other",
];

function ReportDialog({ postId, onClose }) {
  const [reason, setReason] = useState(REPORT_REASONS[0]);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/posts/${postId}/report`, { reason, note });
      if (data.alreadyReported) toast.success("You've already reported this post — thank you.");
      else toast.success("Report submitted — our moderators will review it shortly.");
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not submit report");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      onClick={onClose}
      style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.6)", zIndex: 70, display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}
      data-testid="report-dialog"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="parchment p-6 w-full max-w-md"
        style={{ background: "#15102A", border: "1px solid rgba(212,181,140,0.35)" }}
      >
        <div className="flex justify-between items-start mb-4">
          <div>
            <div className="label-mystic mb-1">Report post</div>
            <h3 className="font-serif text-2xl">Tell us what's wrong</h3>
          </div>
          <button onClick={onClose} aria-label="Close" data-testid="report-dialog-close">
            <X size={18} strokeWidth={1.5} />
          </button>
        </div>

        <div className="space-y-2 mb-4">
          {REPORT_REASONS.map((r) => (
            <label
              key={r}
              className={`flex items-center gap-3 p-3 rounded-sm border cursor-pointer transition-colors ${
                reason === r ? "border-primary bg-primary/10" : "border-secondary/20 hover:border-secondary/40"
              }`}
              data-testid={`report-reason-${r.toLowerCase().replace(/[^a-z]+/g, '-')}`}
            >
              <input
                type="radio"
                name="report-reason"
                value={r}
                checked={reason === r}
                onChange={() => setReason(r)}
                className="accent-primary"
              />
              <span className="text-sm text-foreground/90">{r}</span>
            </label>
          ))}
        </div>

        <textarea
          value={note}
          onChange={(e) => setNote(e.target.value.slice(0, 500))}
          placeholder="Add a note (optional)"
          rows={3}
          maxLength={500}
          className="w-full bg-input/30 border border-secondary/20 rounded-sm p-3 text-sm mb-4"
          data-testid="report-note"
        />

        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="px-5 py-2 rounded-sm uppercase tracking-wider text-xs border border-secondary/30 text-foreground/80"
            data-testid="report-cancel"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={submit}
            disabled={busy}
            className="px-5 py-2 rounded-sm uppercase tracking-wider text-xs bg-destructive text-destructive-foreground disabled:opacity-50"
            data-testid="report-submit"
          >
            {busy ? "Sending…" : "Submit report"}
          </button>
        </div>
      </div>
    </div>
  );
}
